/**
 * Recover a tab that outlived a deploy.
 *
 * Built assets are content-hashed and each deploy replaces them, so a tab
 * opened before a deploy asks for chunk names that no longer exist the first
 * time it lazy-loads something — the import 404s and the screen breaks. A
 * reload picks up the new index.html and its new chunk names.
 *
 * Reload once only: a sessionStorage stamp makes a second failure within the
 * window fall through to the normal error path instead of looping (a chunk
 * that is genuinely broken would otherwise reload forever).
 */

const RELOAD_STAMP_KEY = 'dunea:chunk-reload-at';
const RELOAD_WINDOW_MS = 60_000;

interface ChunkReloadEnv {
  storage: Pick<Storage, 'getItem' | 'setItem'> | null;
  reload: () => void;
  now: () => number;
}

/**
 * Reload for a failed chunk load unless we already did so moments ago.
 * Returns true when a reload was started.
 */
export function reloadOnceForStaleChunk(env: ChunkReloadEnv): boolean {
  const now = env.now();

  // Without storage there is no loop guard, so don't reload at all.
  if (!env.storage) {
    return false;
  }

  try {
    const last = Number(env.storage.getItem(RELOAD_STAMP_KEY) ?? 0);

    if (last && now - last < RELOAD_WINDOW_MS) {
      return false;
    }

    env.storage.setItem(RELOAD_STAMP_KEY, String(now));
  } catch {
    // Storage blocked (private mode / policy): same as no storage.
    return false;
  }

  env.reload();

  return true;
}

/** Listen for Vite's chunk preload failures and reload once. Call before render. */
export function installChunkReloadGuard(): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.addEventListener('vite:preloadError', (event) => {
    let storage: Storage | null = null;

    try {
      storage = window.sessionStorage;
    } catch {
      storage = null;
    }

    const reloading = reloadOnceForStaleChunk({
      storage,
      reload: () => window.location.reload(),
      now: Date.now,
    });

    // Swallow the error only when the reload will replace the page anyway.
    if (reloading) {
      event.preventDefault();
    }
  });
}
